import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const ErrorContainer = styled.div`
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2xl);
  text-align: center;
`;

const ErrorTitle = styled.h2`
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  font-size: var(--fs-2xl);
`;

const ErrorMessage = styled.p`
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xl);
  max-width: 500px;
  line-height: var(--lh-relaxed);
`;

const ErrorButton = styled.button`
  background-color: var(--color-accent-primary);
  color: white;
  border: none;
  padding: var(--spacing-sm) var(--spacing-xl);
  border-radius: 4px;
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--color-accent-secondary);
    transform: translateY(-2px);
  }
`;

const ErrorDetails = styled.details`
  margin-top: var(--spacing-xl);
  padding: var(--spacing-md);
  background: var(--color-primary-light);
  border-radius: 4px;
  max-width: 600px;
  width: 100%;
  
  summary {
    cursor: pointer;
    color: var(--text-muted);
    font-size: var(--fs-sm);
    margin-bottom: var(--spacing-sm);
  }
  
  pre {
    text-align: left;
    font-size: var(--fs-xs);
    overflow-x: auto;
    color: var(--text-secondary);
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      return (
        <ErrorContainer>
          <ErrorTitle>Oops! Something went wrong</ErrorTitle>
          <ErrorMessage>
            We're sorry for the inconvenience. The application encountered an unexpected error. 
            Please try refreshing the page or contact support if the problem persists.
          </ErrorMessage>
          <ErrorButton onClick={this.handleReset}>
            Refresh Page
          </ErrorButton>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <ErrorDetails>
              <summary>Error Details (Development Only)</summary>
              <pre>
                {this.state.error.toString()}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </ErrorDetails>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;